// Assistant engine selection. V1 = the classic polling engine (/assistant/poll);
// V2 = the Pydantic-AI agent behind /api/assistant-v2/chat, streamed into the
// SAME Redux slice so the existing Assistant UI renders it unchanged.
export const ASSISTANT_ENGINES = {
  V1: 'v1',
  V2: 'v2'
}

export const ASSISTANT_ENGINE_STORAGE_KEY = 'scite:assistant:engine'

// Assistant V2 rollout. V2 is the standard engine for B2C (users with no organization
// license), anonymous B2C visitors, and the internal allowlist (scite admins + staff) — all
// get it by default, no flag. B2B (org-licensed) users only see and run V2 when the
// `assistant-v2` PostHog flag opens it to their org; flipping that flag off is the
// kill-switch back to V1 for B2B. An anon visitor matched to an org by campus IP is treated
// as B2B and stays on V1 (no user to resolve the flag against).
const INTERNAL_EMAIL = /@(scite\.ai|researchsolutions\.com)$/i
export const ASSISTANT_V2_FLAG = 'assistant-v2'
const isInternalV2User = user => !!user && (!!user.admin || INTERNAL_EMAIL.test(user.email || ''))
// `runtimeEnabled` = the resolved PostHog `assistant-v2` flag (default OFF); consulted for
// B2B only. `hasOrganizationLicense` = the B2B signal (ui.organizationLicense.hasLicense).
// `licenseChecked` = whether the org-license check has resolved (ui.organizationLicense
// .licenseChecked). Callers resolve the flag via isFeatureFlagEnabled — a saga `call` or the
// useFeatureFlag hook.
export const canUseAssistantV2 = (
  user,
  { runtimeEnabled = false, hasOrganizationLicense = false, licenseChecked = true } = {}
) => {
  // SCI-5113: anonymous (logged-out) visitors run V2's one-free-prompt teaser/unlock funnel,
  // the same as V1 — the backend serves an anon session keyed on the anonId cookie. But an
  // anon visitor on a licensed campus IP is matched as B2B by the IP license check, and we
  // can't resolve that org's assistant-v2 flag without a user — so keep them on V1 (their
  // institutional answer), same as an unflagged B2B user. Deferred until the check resolves.
  if (!user) return licenseChecked && !hasOrganizationLicense
  if (isInternalV2User(user)) return true
  // B2B: only when the org's assistant-v2 flag is up.
  if (hasOrganizationLicense) return runtimeEnabled
  // B2C: on — but only once the license check has resolved. The license defaults to
  // `{ hasLicense: false, licenseChecked: false }` and the check completes asynchronously
  // after user hydration; treating that unresolved window as B2C would briefly hand V2 to an
  // unflagged B2B user. Defer until we actually know (reconcile re-runs on the check).
  return licenseChecked
}

// "Auto" = let the server pick: it runs the caller's per-plan default (free -> the cheap
// tier, Basic/Pro -> the balanced Sonnet). Always selectable, so it is V2's default.
export const ASSISTANT_V2_AUTO_MODEL = 'auto'

// Current-generation lineup Assistant V2 can run (OpenAI GPT-6 + Anthropic Claude
// 5.5-gen), plus "auto". Superseded models are retired from V2's static list: the GPT-5
// snapshots (shutting down), Claude 4.x and Sonnet 5 / Opus 5 (replaced by 5.5 at the
// same or lower price). This is only the picker's fallback while GET /assistant-v2/models
// is in flight (or failed): the server list is authoritative and may still allow older
// picks, and /assistant-v2/chat re-validates every request — nothing here gates what is
// sent. Keep in step with the backend allowlist (ASSISTANT_V2_MODELS in scite-api-fastapi).
export const ASSISTANT_V2_MODEL_VALUES = new Set([
  ASSISTANT_V2_AUTO_MODEL,
  'gpt-6-luna',
  'gpt-6.1-sol',
  'gpt-6-astra',
  'claude-haiku-5-5',
  'claude-sonnet-5-5',
  'claude-opus-5-5',
  'claude-fable-5-1'
])

// The cheapest model from each provider (+ auto) — the only V2 choices on the free plan.
export const ASSISTANT_V2_FREE_MODEL_VALUES = new Set([ASSISTANT_V2_AUTO_MODEL, 'gpt-6-luna', 'claude-haiku-5-5'])

// Basic adds the balanced tier from each provider; the frontier (Opus/Fable/Astra) stays Pro.
export const ASSISTANT_V2_BASIC_MODEL_VALUES = new Set([
  ...ASSISTANT_V2_FREE_MODEL_VALUES,
  'gpt-6.1-sol',
  'claude-sonnet-5-5'
])

// V2's default when the user hasn't (or can't) pick a model: let the server decide.
export const ASSISTANT_V2_DEFAULT_MODEL = ASSISTANT_V2_AUTO_MODEL

// The static allowlist for a plan. `paid` = isPro || isTeam || isEnterprise, `basic` =
// isBasic; the server enforces the same split on /chat (resolve_model_for_plan).
const v2AllowedFor = ({ paid = false, basic = false } = {}) => {
  if (paid) return ASSISTANT_V2_MODEL_VALUES
  return basic ? ASSISTANT_V2_BASIC_MODEL_VALUES : ASSISTANT_V2_FREE_MODEL_VALUES
}

// V2-runnable models the user is allowed to pick, from the static plan allowlist.
export const supportedV2Models = (options, plan = {}) => {
  const allowed = v2AllowedFor(plan)
  return (options || []).filter(o => allowed.has(o.value))
}

// Map the server's GET /assistant-v2/models response ([{ id, free, available }])
// onto MODEL_OPTIONS: keep only the models this user's plan may select, carrying
// the `free` flag through for labeling ("auto" is never badged — it is free for
// everyone by construction). Ordering follows MODEL_OPTIONS (label + speed/intelligence
// metadata live there; the server owns availability). Falls back to the static
// plan-based allowlist while the fetch is in flight (or if it failed), so the picker
// is never empty. Server re-validates on /chat anyway.
export const v2ModelOptionsFrom = (options, availability, plan = {}) => {
  if (!Array.isArray(availability)) return supportedV2Models(options, plan)
  const byId = new Map(availability.map(m => [m.id, m]))
  const selectable = (options || [])
    .filter(o => byId.get(o.value)?.available)
    .map(o => ({ ...o, free: o.value !== ASSISTANT_V2_AUTO_MODEL && !!byId.get(o.value).free }))
  // A server list that yields nothing selectable would empty the picker and coerce
  // the selection to undefined — fall back to the static plan list so it never is.
  return selectable.length ? selectable : supportedV2Models(options, plan)
}

export const normalizeAssistantEngine = engine =>
  engine === ASSISTANT_ENGINES.V2 ? ASSISTANT_ENGINES.V2 : ASSISTANT_ENGINES.V1

export const readStoredAssistantEngine = () => {
  try {
    const stored = typeof window !== 'undefined' && window.localStorage.getItem(ASSISTANT_ENGINE_STORAGE_KEY)
    return stored ? normalizeAssistantEngine(stored) : null
  } catch {
    return null
  }
}

export const storeAssistantEngine = engine => {
  try {
    window.localStorage.setItem(ASSISTANT_ENGINE_STORAGE_KEY, normalizeAssistantEngine(engine))
  } catch {
    // Storage can be unavailable in privacy mode; Redux state still changes.
  }
}

// The pollingData id of an in-flight V2 run (the stream sets it on every progress
// frame; the reducer clears pollingData when the request ends). Also the cancel id.
export const ASSISTANT_V2_RUN_ID = 'assistant-v2'

// Whether a turn came from Assistant V2. V2 streams into the same slice as V1, so a
// persisted/finished V2 answer is recognized by its traces (live) or its saved AI-SDK
// uiMessage (reloaded, before enrichV2Turns rebuilds the traces).
export const isV2Turn = turn => !!(turn?.mcpTraces || turn?.uiMessage || turn?.ui_message)

// Whether new requests go to V2: the persisted toggle, honored only for eligible users
// (the saga applies the same gate before streaming). Drives the V2-only page layout.
export const isAssistantV2Active = (engine, user, { hasOrganizationLicense = false, licenseChecked = true } = {}) =>
  engine === ASSISTANT_ENGINES.V2 && canUseAssistantV2(user, { hasOrganizationLicense, licenseChecked })
